import type { Element, ElementContent, Root, RootContent } from 'hast';

function isImg(node: RootContent | ElementContent): node is Element {
  return node.type === 'element' && node.tagName === 'img';
}

function isWhitespace(node: RootContent | ElementContent): boolean {
  return node.type === 'text' && !node.value.trim();
}

function isImageOnlyParagraph(node: RootContent | ElementContent): node is Element {
  if (node.type !== 'element' || node.tagName !== 'p') return false;
  const meaningful = node.children.filter(c => !isWhitespace(c));
  return meaningful.length === 1 && isImg(meaningful[0]);
}

function wrapInParagraph(img: Element): Element {
  return { type: 'element', tagName: 'p', properties: {}, children: [img] };
}

/**
 * Extract images out of mixed-content paragraphs (text + images).
 * Each image becomes its own sibling `<p>` after the text paragraph.
 * This prevents `<div>` inside `<p>` hydration errors and enables
 * the grouping pass to detect consecutive images.
 */
function splitMixedImageParagraphs(parent: Root | Element) {
  const { children } = parent;
  let i = 0;

  while (i < children.length) {
    const child = children[i];
    if (child.type !== 'element' || child.tagName !== 'p') {
      i++;
      continue;
    }

    const imgs = child.children.filter(c => isImg(c)) as Element[];
    if (imgs.length === 0 || !child.children.some(c => !isImg(c) && !isWhitespace(c))) {
      i++;
      continue;
    }

    // Keep non-image content, trim trailing whitespace
    child.children = child.children.filter(c => !isImg(c));
    while (child.children.length > 0 && isWhitespace(child.children.at(-1)!)) {
      child.children.pop();
    }

    const imgParagraphs = imgs.map(wrapInParagraph);
    children.splice(i + 1, 0, ...imgParagraphs);
    i += 1 + imgParagraphs.length;
  }
}

/** Group consecutive image-only paragraphs into `<image-gallery>` elements. */
function groupConsecutiveImages(parent: Root | Element) {
  const { children } = parent;
  let i = 0;

  while (i < children.length) {
    if (!isImageOnlyParagraph(children[i])) {
      i++;
      continue;
    }

    let end = i + 1;
    while (
      end < children.length &&
      (isImageOnlyParagraph(children[end]) || isWhitespace(children[end]))
    ) {
      end++;
    }

    const imgs = children
      .slice(i, end)
      .filter((c): c is Element => isImageOnlyParagraph(c))
      .map(p => p.children.find(c => isImg(c)) as Element);

    if (imgs.length >= 2) {
      const gallery: Element = {
        type: 'element',
        tagName: 'image-gallery',
        properties: {},
        children: imgs,
      };
      children.splice(i, end - i, gallery);
    }
    i++;
  }
}

function visit(node: Root | Element) {
  splitMixedImageParagraphs(node);
  groupConsecutiveImages(node);
  for (const child of node.children) {
    if (child.type === 'element') visit(child);
  }
}

export function rehypeImageGallery() {
  return (tree: Root) => visit(tree);
}
